import React, { useState, useEffect } from 'react';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import type { Asset } from '../../../types';

interface ProgressiveAssetLoaderProps {
  asset: Asset;
  renderEmbed: (asset: Asset, isLoaded: boolean) => React.ReactNode;
  priority?: boolean;
  delay?: number;
}

export const ProgressiveAssetLoader: React.FC<ProgressiveAssetLoaderProps> = ({
  asset,
  renderEmbed,
  priority = false,
  delay = 0
}) => {
  const [isLoaded, setIsLoaded] = useState(priority);
  const [shouldLoad, setShouldLoad] = useState(priority);
  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '100px'
  });

  useEffect(() => {
    if (isIntersecting && !shouldLoad) {
      if (delay > 0) {
        const timer = setTimeout(() => {
          setShouldLoad(true);
        }, delay);
        return () => clearTimeout(timer);
      } else {
        setShouldLoad(true);
      }
    }
    return undefined;
  }, [isIntersecting, shouldLoad, delay]);

  useEffect(() => {
    if (shouldLoad && !isLoaded) {
      // Simulate loading time for heavy embeds
      const loadingTime = Math.random() * 1000 + 500; // 500-1500ms
      const timer = setTimeout(() => {
        setIsLoaded(true);
      }, loadingTime);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [shouldLoad, isLoaded]);

  return (
    <div ref={elementRef as React.RefObject<HTMLDivElement>} className="asset-container">
      {shouldLoad ? (
        <div className={`asset-content ${isLoaded ? 'loaded' : 'loading'}`}>
          {renderEmbed(asset, isLoaded)}
        </div>
      ) : (
        <div className="asset-placeholder">
          <div className="skeleton-loader" />
        </div>
      )}
    </div>
  );
};
